import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  Box,
  TextField,
  MenuItem,
  Grid,
  Button,
  Typography,
} from '@mui/material';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';

import { AccountGroup } from '../../../../shared/services/types/response';
import DatePickerComponent from '../datePickerComponent';
import { CreatePersonGroupDTO } from '../modalDetails/service';
import { useState } from 'react';
import { maskCPFCNPJ } from '../../../../shared/utils/mask';

interface Props {
  group: AccountGroup;
  tipoPessoa: { label: string; value: string }[];
  tipoVinculo: { label: string; value: string }[];
  createPersonGroup: (
    groupId: string,
    values: CreatePersonGroupDTO,
  ) => Promise<{ success: boolean; message?: string }>;
  triggerReload: () => void; // <- aqui está a função para acionar o reload
  onPersonCreated?: () => void; // <- opcional e reutilizável
}

const PersonGroupForm = ({
  group,
  tipoPessoa,
  tipoVinculo,
  createPersonGroup,
  triggerReload,
  onPersonCreated,
}: Props) => {
  const [isLoading, setIsLoading] = useState(false);
  const formik = useFormik({
    initialValues: {
      type_person: '',
      person_name: '',
      cnpj_cpf_person: '',
      type_linked_bdv: '',
      end_date: '',
      start_date: '',
    },
    validationSchema: Yup.object({
      type_person: Yup.string().required('Tipo pessoa é obrigatório'),
      person_name: Yup.string().required('Nome é obrigatório'),
      cnpj_cpf_person: Yup.string().required('CPF ou CNPJ é obrigatório'),
      type_linked_bdv: Yup.string().required(
        'Tipo de vínculo BDV é obrigatório',
      ),
      start_date: Yup.string().required('Data início é obrigatória'),
    }),
    onSubmit: async (values) => {
      setIsLoading(true);
      try {
        const { success, message = 'Erro desconhecido' } =
          await createPersonGroup(group.id, values);
        if (success) {
          toast.success('Nova pessoa criada com sucesso');
          await new Promise((resolve) => setTimeout(resolve, 300)); // dá tempo para o backend salvar
          triggerReload(); // dispara reload no pai
          onPersonCreated?.(); // <-- dispara a flag
        } else {
          toast.error(`Erro ao criar nova pessoa: ${message}`);
        }
      } catch (error) {
        console.error('Erro ao criar pessoa:', error);
        toast.error('Erro inesperado ao criar pessoa');
      } finally {
        setIsLoading(false);
        triggerReload();
      }
    },
  });

  return (
    <Box component="form" onSubmit={formik.handleSubmit}>
      <Box display="flex" flexDirection="column" gap={2}>
        <Typography variant="h6" sx={{ mt: 2 }}>
          Nova Pessoa
        </Typography>

        <TextField
          select
          fullWidth
          disabled={isLoading}
          label="Tipo de Pessoa"
          name="type_person"
          value={formik.values.type_person}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={
            formik.touched.type_person && Boolean(formik.errors.type_person)
          }
          helperText={formik.touched.type_person && formik.errors.type_person}
        >
          {tipoPessoa.map((item) => (
            <MenuItem key={item.value} value={item.value}>
              {item.label}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          disabled={isLoading}
          fullWidth
          label="Nome"
          name="person_name"
          value={formik.values.person_name}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={
            formik.touched.person_name && Boolean(formik.errors.person_name)
          }
          helperText={formik.touched.person_name && formik.errors.person_name}
        />

        <TextField
          disabled={isLoading}
          fullWidth
          label="CPF ou CNPJ"
          name="cnpj_cpf_person"
          value={maskCPFCNPJ(formik.values.cnpj_cpf_person)}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={
            formik.touched.cnpj_cpf_person &&
            Boolean(formik.errors.cnpj_cpf_person)
          }
          helperText={
            formik.touched.cnpj_cpf_person && formik.errors.cnpj_cpf_person
          }
          inputProps={{ maxLength: 18 }} // 14 dígitos + máscara (pontuação)
        />

        <TextField
          disabled={isLoading}
          select
          fullWidth
          label="Tipo de Vínculo BDV"
          name="type_linked_bdv"
          value={formik.values.type_linked_bdv}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={
            formik.touched.type_linked_bdv &&
            Boolean(formik.errors.type_linked_bdv)
          }
          helperText={
            formik.touched.type_linked_bdv && formik.errors.type_linked_bdv
          }
        >
          {tipoVinculo.map((item) => (
            <MenuItem key={item.value} value={item.value}>
              {item.label}
            </MenuItem>
          ))}
        </TextField>

        <Grid sx={{ display: 'flex', justifyContent: 'row', gap: 2 }}>
          <DatePickerComponent
            isLoading={isLoading}
            label="Data Início"
            value={
              formik.values.start_date ? dayjs(formik.values.start_date) : null
            }
            onChange={(newValue) =>
              formik.setFieldValue('start_date', newValue)
            }
            error={formik.errors.start_date}
            touched={formik.touched.start_date}
          />

          <DatePickerComponent
            isLoading={isLoading}
            label="Data Final"
            value={
              formik.values.end_date ? dayjs(formik.values.end_date) : null
            }
            onChange={(newValue) => formik.setFieldValue('end_date', newValue)}
            error={formik.errors.end_date}
            touched={formik.touched.end_date}
          />
        </Grid>
        <Grid
          sx={{
            display: 'flex',
            justifyContent: 'end',
          }}
        >
          <Button
            type="button"
            variant="contained"
            onClick={() => formik.handleSubmit()}
            color="primary"
            disabled={isLoading}
          >
            Salvar
          </Button>
        </Grid>
      </Box>
    </Box>
  );
};

export default PersonGroupForm;
